import type { MatchRepository, SavedMatch } from '@texas42/app-model';
import type { Texas42Database } from '../db.js';
import { STORE_MATCHES } from '../constants.js';
import { listEnvelopes, readEnvelopeOrNull, writeEnvelope } from '../envelope-io.js';
import type { WriteQueue } from '../write-queue.js';
import { computeChecksum } from '../checksum.js';
import { createEnvelope } from '../records.js';
import { StorageUnavailableError } from '../storage-errors.js';

export function createIndexedDbMatchRepository(
  db: Texas42Database,
  queue: WriteQueue,
): MatchRepository {
  return {
    async save(match) {
      await writeEnvelope(queue, db, STORE_MATCHES, match);
    },

    async get(id) {
      return readEnvelopeOrNull<SavedMatch>(db, STORE_MATCHES, id, 'match', id);
    },

    async list() {
      const matches = await listEnvelopes<SavedMatch>(db, STORE_MATCHES, 'match', (m) => m.id);
      return matches.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    },

    async delete(id) {
      await queue.enqueue(async () => {
        try {
          await db.delete(STORE_MATCHES, id);
        } catch (error) {
          throw new StorageUnavailableError('Unable to delete saved match.', error);
        }
      });
    },

    async rename(id, name) {
      const existing = await readEnvelopeOrNull<SavedMatch>(db, STORE_MATCHES, id, 'match', id);
      if (!existing) return;

      const updated: SavedMatch = {
        ...existing,
        name,
        updatedAt: new Date().toISOString(),
      };

      await queue.enqueue(async () => {
        const checksum = await computeChecksum(updated);
        await db.put(STORE_MATCHES, createEnvelope(updated, checksum));
      });
    },
  };
}
